/**
 * `GET /api/agents`: the agents' labels, greetings and starters, plus the
 * caller's practice scope. One TanStack query shared by every consumer and
 * persisted to the user's IndexedDB cache, so a hard load renders from the
 * last catalog while it revalidates.
 */
import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";

import { queryClient } from "@/lib/query/queryClient";

import { fetchAgentSpecs } from "./api/agentApi";
import {
  normalizeAgentKind,
  type AgentKind,
  type AgentModel,
  type AgentScope,
  type AgentSpec,
  type AgentsCatalog,
  type AutoMode
} from "./types";

const NO_MODELS: AgentModel[] = [];
const NO_AUTO_MODES: AutoMode[] = [];

export const AGENT_SPECS_QUERY_KEY = ["agents"] as const;
export const AGENT_SPECS_STALE_MS = 10 * 60 * 1000;

/** Test seam: forget the cached catalog. */
export const resetAgentSpecsCache = (): void => {
  queryClient.removeQueries({ queryKey: AGENT_SPECS_QUERY_KEY });
};

export type AgentSpecsState = {
  catalog: AgentsCatalog | null;
  loading: boolean;
  error: string | null;
  specFor: (kind: AgentKind) => AgentSpec | null;
  /** The caller's practice scope, once known. */
  practiceScope: AgentScope | null;
  /** The model picker's entries (configured providers only). */
  models: AgentModel[];
  /** Auto's Optimize For modes. */
  autoModes: AutoMode[];
};

export const useAgentSpecs = (): AgentSpecsState => {
  const query = useQuery(
    {
      queryKey: AGENT_SPECS_QUERY_KEY,
      queryFn: fetchAgentSpecs,
      staleTime: AGENT_SPECS_STALE_MS,
      meta: { persist: true }
    },
    queryClient
  );
  const catalog = query.data ?? null;
  const error = query.error
    ? (query.error as Error).message ?? "Couldn't load agents"
    : null;

  const practice = catalog?.practice ?? null;
  // Stable identities: callers key effects and memos on these.
  const practiceScope = useMemo<AgentScope | null>(
    () =>
      practice
        ? {
            kind: "practice",
            id: practice.scope_id,
            displayName: practice.display_name
          }
        : null,
    [practice]
  );
  // By kind first; a catalog from before the merge lists `client_manager` /
  // `case_assistant` instead of `assistant`, so fall back to an entry whose
  // kind or aliases normalize to the one asked for.
  const specFor = useCallback(
    (kind: AgentKind) =>
      catalog?.agents.find((a) => a.kind === kind) ??
      catalog?.agents.find(
        (a) =>
          a.aliases?.includes(kind) ||
          normalizeAgentKind(a.kind, "skill_builder") === kind
      ) ??
      null,
    [catalog]
  );
  return {
    catalog,
    loading: !catalog && !error,
    error,
    specFor,
    practiceScope,
    models: catalog?.models ?? NO_MODELS,
    autoModes: catalog?.auto_modes ?? NO_AUTO_MODES
  };
};
